import { useCallback, useMemo } from 'react';
import { toUpperCase } from 'utils/toLowerCase';
import operateBtns from 'components/Refund/OperateBtns';
import { useTradeListContext } from '../TradeContext';

/** 操作按钮入参类型（与 OperateBtns 保持一致） */
export type ReturnOperationConfig = Parameters<typeof operateBtns>[0];

/**
 * 退货单操作按钮配置：组装 returnOrderItemId、refresh 回调及 operateBtns 所需参数
 */
export function useReturnOperationConfig(
  sn: string,
  conversation: Conversation.Conversation,
  trade: Conversation.Trade.TradeWithLogistics,
  data: Conversation.Trade.ReturnProduct[],
  refundReason: Conversation.Trade.RefundReason,
) {
  const { refreshTrade } = useTradeListContext();
  const { storeId: internalStoreId } = conversation;
  const {
    externalShopId,
    externalTradeId,
    platform,
    internalTradeId,
    unit,
    region,
    internalBuyerId,
  } = trade;
  const {
    operate,
    displayStatus,
    refundAmount,
    latestOfferAmount,
    allowOffer,
    offerCounterLimit,
    preDispute,
  } = refundReason;

  const returnOrderItemId = useMemo(
    () => data.map((item) => item.refundReason?.[0]?.returnOrderItemId ?? '').filter(Boolean),
    [data],
  );

  const refreshList = useCallback(() => {
    refreshTrade(
      {
        page_no: 1,
        page_size: 1,
        internal_buyer_id: internalBuyerId,
        internal_store_id: internalStoreId,
        external_trade_ids: externalTradeId,
      },
      'SHOPEE',
    );
  }, [refreshTrade, internalBuyerId, internalStoreId, externalTradeId]);

  const operationConfig = useMemo<ReturnOperationConfig>(
    () => ({
      type: 'primary',
      externalStoreId: externalShopId,
      externalTradeId,
      platform: toUpperCase(platform),
      returnOrderItemId,
      returnSn: sn,
      operate,
      refreshList,
      internalStoreId,
      internalTradeId,
      displayStatus,
      unit,
      refundAmount,
      latestOfferAmount,
      allowOffer,
      offerCounterLimit,
      operationLog: [],
      preDispute,
      region,
    }),
    [
      externalShopId,
      externalTradeId,
      platform,
      returnOrderItemId,
      sn,
      operate,
      refreshList,
      internalStoreId,
      internalTradeId,
      displayStatus,
      unit,
      refundAmount,
      latestOfferAmount,
      allowOffer,
      offerCounterLimit,
      preDispute,
      region,
    ],
  );

  return { operationConfig };
}
